import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, ArrowUp, Check, Maximize2, Minimize2, Minus, RefreshCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// The crane mark stands in for the old generic Bot glyph — brand, not clip-art.
const CRANE_MARK = '/favicon.png';
import { useI18n } from '../i18n';
import { useRecoSession } from '../context/RecoSessionProvider';
import { AgentMarkdown } from './AgentMarkdown';
import AddressExtentPicker from './map';
import ImageryPreviewPanel from './ImageryPreviewPanel';

// The one filled, accent-coloured control. At most one is on screen at a time —
// that is what keeps a single accent legible as "the thing to press".
const BTN_PRIMARY =
  'flex-1 rounded-lg bg-crane-500 px-3 py-2 text-ui font-medium text-ink-950 transition-colors ' +
  'hover:bg-crane-600 disabled:cursor-not-allowed disabled:opacity-40 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-400';

const BTN_SECONDARY =
  'flex-1 rounded-lg border border-ink-600 px-3 py-2 text-ui font-medium text-ink-300 transition-colors ' +
  'hover:border-ink-500 hover:text-ink-100 disabled:cursor-not-allowed disabled:opacity-40 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500';

// Quieter than BTN_SECONDARY — for a decline sitting beside the action we
// actually want, where an equal-weight outline reads as a real alternative.
/**
 * Cut positions that never fall inside a `**bold**` span.
 *
 * Revealing a message character by character would otherwise render its own
 * syntax: "captured **March" shows the asterisks until the closing pair
 * arrives. Bold spans are therefore revealed whole — a slight stutter in the
 * cadence, and no flicker of markup.
 */
function markdownSafeCuts(text: string): number[] {
  const cuts: number[] = [];
  let i = 0;
  let inBold = false;
  while (i <= text.length) {
    if (text.startsWith('**', i)) { inBold = !inBold; i += 2; continue; }
    if (!inBold) cuts.push(i);
    i += 1;
  }
  if (cuts[cuts.length - 1] !== text.length) cuts.push(text.length);
  return cuts;
}

/** Roughly a fast typist. Slow enough to follow, brisk enough not to wait on. */
const TYPE_CHARS_PER_SECOND = 90;

/**
 * Reveals a message as it would arrive if Reco were typing it.
 *
 * The point is not decoration: a bubble that appears whole gives the eye nothing
 * to track, so a transcript that has just scrolled looks like a transcript that
 * has just jumped. Text that grows drags the scroll along with it at a speed a
 * reader can follow.
 *
 * rAF rather than an interval, so it keeps step with the display and stops
 * dead while the tab is hidden instead of racing to catch up on return.
 */
const TypedMarkdown: React.FC<{ text: string; onDone: () => void }> = ({ text, onDone }) => {
  const cuts = useRef(markdownSafeCuts(text));
  const [shown, setShown] = useState(0);
  const doneRef = useRef(onDone);
  useEffect(() => { doneRef.current = onDone; });

  useEffect(() => {
    let raf = 0;
    let start = 0;
    const step = (t: number) => {
      if (!start) start = t;
      const want = ((t - start) / 1000) * TYPE_CHARS_PER_SECOND;
      // Snap to the next safe cut at or beyond the character budget.
      const next = cuts.current.find((c) => c >= want) ?? text.length;
      setShown(next);
      if (next >= text.length) { doneRef.current(); return; }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [text]);

  return <AgentMarkdown text={text.slice(0, shown)} />;
};

/** Inert callback for transcript copies of a control that has nothing left to say. */
const noop = () => {};

/** Shared by the three contact inputs, so they cannot drift apart. */
const FIELD_CLASS =
  'w-full rounded-lg border border-ink-600 bg-ink-850 px-3 py-2 text-body text-ink-100 ' +
  'caret-crane-500 outline-none transition-colors placeholder:text-ink-500 ' +
  'focus:border-crane-500/60 disabled:cursor-not-allowed disabled:opacity-50';

const BTN_QUIET =
  'flex-1 rounded-lg border border-ink-700 px-3 py-2 text-ui font-medium text-ink-400 transition-colors ' +
  'hover:border-ink-600 hover:text-ink-200 disabled:cursor-not-allowed disabled:opacity-40 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500';

/**
 * An icon-only header control with a hover/focus label.
 *
 * The icon carries no text, so it needs one on hover. A native title= waits
 * about a second and paints in OS chrome; this shows immediately and in the
 * panel's own palette. aria-label already names the button, so the bubble is
 * decorative to a reader.
 */
const IconAction: React.FC<{
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}> = ({ onClick, label, children }) => (
  <div className="group relative flex-shrink-0">
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-8 w-8 items-center justify-center rounded-lg border border-ink-600 bg-ink-850 text-ink-200 transition-colors hover:border-crane-500/60 hover:bg-ink-800 hover:text-crane-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500"
    >
      {children}
    </button>
    <span
      aria-hidden
      className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 whitespace-nowrap rounded-md border border-ink-700 bg-ink-900 px-2 py-1 text-meta text-ink-200 opacity-0 shadow-lg shadow-black/40 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
    >
      {label}
    </span>
  </div>
);

// ─── Inner panel ─────────────────────────────────────────────────────────────

export type ReconstructionAgentVariant = 'floating' | 'inline';

interface ReconstructionAgentProps {
  /** 'floating' pins the widget to the viewport corner (landing page).
   *  'inline' renders it in normal flow, centred, for the dedicated page. */
  variant?: ReconstructionAgentVariant;
}

/** A callback with a stable identity that always calls the latest closure. */
function useCallbackRef<T extends (...args: never[]) => unknown>(fn: T): T {
  const ref = useRef(fn);
  useEffect(() => { ref.current = fn; });
  return useRef(((...args: never[]) => ref.current(...args)) as T).current;
}

const ReconstructionAgentPanel: React.FC<ReconstructionAgentProps> = ({ variant = 'floating' }) => {
  const isInline = variant === 'inline';
  const { lang, t, toggle } = useI18n();
  const navigate = useNavigate();
  const ra = t.reconstructionAgent;

  /*
   * The conversation lives above the router, so it survives the trip between
   * the landing-page widget and /reco. Only how it is drawn is local to here.
   */
  const {
    tab, radiusM, setRadiusM, phase, messages, input, setInput, loading, error,
    checkPassed, pendingWarning, warningActionLoading, vexcelCoverage, setVexcelCoverage,
    vexcelProceedLoading,
    inputRef,
    checkCoverage, handleVexcelCancel,
    coverageConfirmed, showChoices,
    nameInput, setNameInput, companyInput, setCompanyInput, cancelKind,
    previewReady, onPreviewReady, onPreviewFailed, acceptPreview, declinePreview, submitCancelReason,
    emailInput, setEmailInput, emailError, submitEmail,
    handleProceed, handleRevise, resetSession, chooseOption, send,
    conversationStarted, sessionFresh, doneKind,
  } = useRecoSession();

  /*
   * Index of the message being revealed, if any. Only ever the newest assistant
   * turn, and only when it arrives while we are watching: a restored transcript
   * or a navigation must not replay itself.
   */
  const [typingIndex, setTypingIndex] = useState<number | null>(null);
  /*
   * How many messages are on screen. Usually all of them; it lags behind while
   * a queue is being revealed one at a time.
   *
   * A turn frequently appends more than one message — the coverage step adds
   * the settled extent, the verdict on it, and then the "verifying" line — and
   * React batches those into a single commit. Watching only the newest message
   * therefore animated the last of the three and dropped the other two in
   * whole. Revealing in order is both the fix and the right behaviour: they
   * were written to be read in sequence.
   */
  const [revealed, setRevealed] = useState<number | null>(null);
  const typingRef = useRef(false);
  typingRef.current = typingIndex !== null;

  /** Everything at once, now — for a click, or when animation is unwanted. */
  const revealAll = useCallback(() => {
    setTypingIndex(null);
    setRevealed(messages.length);
  }, [messages.length]);

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // First sight of this session.
    if (revealed === null) {
      /*
       * A transcript that arrives already populated is a restored one and must
       * not replay itself — except when it is nothing but the opening greeting.
       * Two paths reach /reco: a direct load, where the session seeds after this
       * mounts, and a navigation from the landing page, where the greeting was
       * seeded there and is already present. To the reader the page has only
       * just opened either way.
       */
      const onlyGreeting = messages.length === 1 && messages[0]?.role === 'assistant';
      if (reduced || !onlyGreeting) { setRevealed(messages.length); return; }
      setRevealed(1);
      setTypingIndex(0);
      return;
    }

    if (reduced) { setRevealed(messages.length); return; }
    // A reset shortens the transcript; never leave `revealed` past the end.
    if (messages.length < revealed) { setRevealed(messages.length); setTypingIndex(null); return; }
    if (typingIndex !== null || revealed >= messages.length) return;

    // Reveal the next one. Only an assistant message with words gets typed;
    // a user turn or an attachment-only message appears at once and the queue
    // moves straight on.
    const next = messages[revealed];
    setRevealed(revealed + 1);
    if (next?.role === 'assistant' && next.content) setTypingIndex(revealed);
  }, [messages, revealed, typingIndex]);

  /*
   * Which of the two rest states the floating widget starts in on a fresh
   * session: the crane mark alone, or Reco's opening bubble beside it.
   *
   * The mark, currently — the bubble sat over the hero's own call to action and
   * introduced Reco before anyone had asked. The widget is one tap away either
   * way; this only changes what greets you. Flip this one constant to put the
   * bubble back; both rest states are kept and neither is otherwise touched.
   */
  const RESTS_AS_MARK = true;

  /*
   * Either rest state applies only to a session that has not started. Coming
   * back from /reco mid-conversation it has to open as the panel: the peek
   * bubble renders the canned greeting and the mark hides everything, so resting
   * in either would suggest the chat was lost while the transcript sat intact
   * behind it.
   */
  const restsOnLoad = variant !== 'inline' && sessionFresh;
  const [minimized, setMinimized]   = useState(() => RESTS_AS_MARK && restsOnLoad);
  const [peek, setPeek]             = useState(() => !RESTS_AS_MARK && restsOnLoad);
  // Hover-to-open only where hovering is a real gesture. On touch a synthesised
  // mouseenter fires with the tap that is already opening it, and on a stylus
  // it fires on hover with no way to dismiss.
  const [pointerFine] = useState(
    () => typeof window !== 'undefined'
      && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches,
  );
  const peekHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peekCollapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** True only while the panel is open because the pointer wandered over it. */
  const openedByHoverRef = useRef(false);

  const bottomRef = useRef<HTMLDivElement>(null);

  /*
   * Following the transcript.
   *
   * The transcript is its own scroller in both presentations, so this only ever
   * moves that element — never the document. In the floating widget the panel is
   * `fixed` and would not move with the page anyway; inline, scrolling the page
   * is exactly what the viewport-height layout exists to prevent.
   */
  const scrollerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const didAnchorRef = useRef(false);
  /** Was the reader at the end before the last layout change? */
  const atEndRef = useRef(true);

  /*
   * True from the moment we ask for a scroll until it lands. A smooth scroll
   * emits `scroll` events all the way down, and every one of them reports a
   * position short of the end — so reading `atEndRef` from them makes our own
   * animation look like a reader who has scrolled away.
   */
  const autoScrollingRef = useRef(false);

  const scrollToEnd = useCallbackRef((behavior: ScrollBehavior) => {
    const el = scrollerRef.current;
    if (!el) return;
    autoScrollingRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior });
    atEndRef.current = true;
  });

  /**
   * Within a bubble's height of the end — close enough that following is wanted.
   *
   * Only a reader may switch following *off*. This fired mid-animation and
   * detached the transcript permanently: approving the previews appends three
   * things at once — the approval, the carousel, and the email ask — which in
   * the floating widget is more than the 330px scroller holds. The smooth scroll
   * to the end passed through positions hundreds of pixels short, one of those
   * `scroll` events set `atEndRef` false, and the ResizeObserver below then
   * declined to follow anything further. The new bubbles typed themselves out
   * under the composer, out of sight, which read as a chat that had stopped
   * animating with the email field sitting on top of it.
   */
  const onScroll = useCallbackRef(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 140) {
      atEndRef.current = true;
      autoScrollingRef.current = false;   // landed
      return;
    }
    if (autoScrollingRef.current) return; // still ours, not theirs
    atEndRef.current = false;
  });

  /*
   * A gesture hands the scroller back. Without this a scroll we started but
   * never finished — content growing under a smooth animation cancels it — would
   * keep claiming every subsequent event, and someone scrolling up to re-read
   * would be dragged to the end again.
   */
  const onUserScrollIntent = useCallbackRef(() => { autoScrollingRef.current = false; });

  /*
   * `revealed`, not just `messages`. A turn appends every message at once but
   * they reach the screen one at a time, so scrolling on the array scrolled to a
   * transcript that had not grown yet — the height it needed arrived later, with
   * nothing left to trigger a second pass.
   */
  useEffect(() => {
    const first = !didAnchorRef.current;
    didAnchorRef.current = true;
    scrollToEnd(first ? 'auto' : 'smooth');
  }, [messages, revealed, phase, scrollToEnd]);

  /*
   * Two things move the ground after a message lands, and both leave the last
   * bubble half-covered:
   *
   *   - content inside the transcript grows, because a map or an image finishes
   *     laying out after the message carrying it was appended;
   *   - the *composer* grows — the extent card and the preview carousel render
   *     inside it and can add hundreds of pixels — which shrinks the scroller
   *     under a scrollTop that was correct a moment earlier.
   *
   * The second is why the "Found it" bubble ended up behind the map card: the
   * scroll ran, then the card mounted and took the room. Observing the scroller
   * as well as the list catches it. Only for a reader already at the end, so it
   * never yanks the view from someone reading further up.
   */
  /*
   * Held in a ref and wired through callback refs rather than looked up once.
   *
   * This used to read both elements in a mount effect and bail if either was
   * missing — which is exactly what happens in the floating widget: it mounts in
   * its resting peek state, which returns early and renders neither the
   * transcript nor its scroller. The effect found nulls, returned, and never ran
   * again, because its only dependency was a stable callback. Opening the panel
   * attached the refs to nothing. The widget therefore had no size-following at
   * all, while /reco — which renders the panel immediately — worked, and the
   * difference looked like a bug in the chat rather than in the observer.
   */
  const roRef = useRef<ResizeObserver | null>(null);

  const observe = useCallback((ref: React.MutableRefObject<HTMLDivElement | null>) =>
    (el: HTMLDivElement | null) => {
      if (ref.current && roRef.current) roRef.current.unobserve(ref.current);
      ref.current = el;
      if (el && roRef.current) roRef.current.observe(el);
    }, []);
  const setScroller = useMemo(() => observe(scrollerRef), [observe]);
  const setList     = useMemo(() => observe(listRef), [observe]);

  useEffect(() => {
    /*
     * 'auto' only while a message is being typed. The transcript grows a few
     * pixels per frame there, and an instant correction reads as continuous
     * follow — a smooth one would restart its easing every frame.
     *
     * Otherwise 'smooth'. This was unconditionally instant, which meant
     * appending a bubble fired the observer a frame after the message effect
     * had asked for a smooth scroll, cancelling it outright: the transcript
     * always jumped, and nothing showed the reader that it had moved.
     */
    const ro = new ResizeObserver(() => {
      if (atEndRef.current) scrollToEnd(typingRef.current ? 'auto' : 'smooth');
    });
    roRef.current = ro;
    // The inline variant is already mounted by the time this runs; the widget's
    // panel arrives later and registers itself through the callback refs.
    if (listRef.current) ro.observe(listRef.current);
    if (scrollerRef.current) ro.observe(scrollerRef.current);
    return () => { ro.disconnect(); roRef.current = null; };
  }, [scrollToEnd]);

  useEffect(() => {
    return () => {
      if (peekHoverTimer.current) clearTimeout(peekHoverTimer.current);
      if (peekCollapseTimer.current) clearTimeout(peekCollapseTimer.current);
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const backdropStyle: React.CSSProperties = {
    backdropFilter: 'blur(12px) saturate(160%)',
    WebkitBackdropFilter: 'blur(12px) saturate(160%)',
  };

  const inputDisabled = loading || phase === 'connecting' || phase === 'vexcel-confirm'
    || phase === 'imagery-preview' || phase === 'cancel-reason' || phase === 'email-capture';

  // The text row hides while a decision card owns the turn.
  /* 'done' keeps the composer: the job is queued, not the conversation. Someone
     who wants a second location, or just has a question, should not have to
     restart the session to ask it. */
  const showComposer = !pendingWarning
    && phase !== 'vexcel-confirm' && phase !== 'imagery-preview'
    && phase !== 'cancel-reason' && phase !== 'email-capture';

  /*
   * Has the transcript finished arriving?
   *
   * Every decision control waits for this. They are introduced by a message —
   * "I found it… select the area", "What made you decide not to go ahead?", "I'm
   * ready to queue your reconstruction" — but they render on the *phase* change,
   * which happens the moment the message is appended rather than when it has
   * been read. So each card, form and button row appeared first and its own
   * explanation typed itself out underneath afterwards.
   *
   * The carousel was fixed on its own and this is the same bug everywhere else,
   * so it is one rule now: nothing that asks something of the reader appears
   * until the sentence asking it has landed.
   *
   * `revealed === null` is the very first render, before the queue starts; there
   * is nothing outstanding then, so it counts as settled.
   */
  const transcriptSettled = typingIndex === null
    && (revealed === null || revealed >= messages.length);

  // Anything awaiting the user renders inside the composer box, never in the transcript.
  const pendingAction = transcriptSettled && (
    !!pendingWarning ||
    (phase === 'vexcel-confirm' && !!vexcelCoverage) ||
    phase === 'imagery-preview' ||
    phase === 'cancel-reason' ||
    phase === 'email-capture' ||
    (phase === 'intake' && tab !== null)
  );

  // A choice must be made before typing helps — the highlight moves to the choices.
  // Deliberately not gated on `loading`: the choices stay put while a reply is in
  // flight and simply go inert, rather than unmounting and remounting each turn.
  // The opening choices belong to the first turn and nothing else. Keying them
  // on `tab` alone was wrong: `tab` is only ever set by clicking a card, so a
  // user who typed their address instead left it null forever, and the cards
  // came back every time the flow returned to intake — most visibly after
  // confirming an extent, while Reco was asking for an email.
  const awaitingChoice = transcriptSettled
    && phase === 'intake' && tab === null && (!conversationStarted || showChoices);

  // Focus follows availability: whenever the field becomes usable, the caret is
  // already in it, so there is never a question of where to type.
  useEffect(() => {
    // Never while the opening choices are up: a caret blinking in the text field
    // competes with them, which is what made it unclear whether to click or type.
    if (awaitingChoice) return;
    if (showComposer && !inputDisabled) inputRef.current?.focus();
  }, [showComposer, inputDisabled, phase, tab, awaitingChoice, inputRef]);
  // One chip, one colour — the phase only swaps its label. This replaced four
  // different tint/border pairs (emerald, gold, blue, amber) and the raw internal
  // system names "Yugo" and "Vexcel", which meant nothing to a user.
  // Null where the surface below already says it: the extent card is itself the
  // instruction to choose an area, so a chip repeating that was noise.
  // No chip at 'done': the closing message already says the job is queued and
  // where the notification goes, so "Received" only added an unexplained pill.
  // The transient phases keep theirs — those say something the transcript does not.
  const statusLabel =
    phase === 'si-relay' ? ra.statusProcessing :
    phase === 'connecting' ? ra.statusChecking :
                             null;

  const inputPlaceholder =
    phase === 'connecting'     ? ra.checkingPlaceholder :
    phase === 'vexcel-confirm' ? ra.awaitingConfirm :
    tab === 'address'          ? ra.addressPlaceholder :
    ra.placeholder;

  // ── Minimized pill ──────────────────────────────────────────────────────────
  // A short dwell before opening, so crossing the corner with the pointer on the
  // way somewhere else does not fling the panel open.
  const openFromPeek = (viaHover = false) => {
    if (peekHoverTimer.current) clearTimeout(peekHoverTimer.current);
    peekHoverTimer.current = null;
    openedByHoverRef.current = viaHover;
    setPeek(false);
  };
  const onPeekHover = () => {
    if (!pointerFine || peekHoverTimer.current) return;
    peekHoverTimer.current = setTimeout(() => openFromPeek(true), 260);
  };
  const cancelPeekHover = () => {
    if (peekHoverTimer.current) clearTimeout(peekHoverTimer.current);
    peekHoverTimer.current = null;
  };


  const onPanelEnter = () => {
    if (peekCollapseTimer.current) clearTimeout(peekCollapseTimer.current);
    peekCollapseTimer.current = null;
  };
  const onPanelLeave = () => {
    if (isInline || !pointerFine || !openedByHoverRef.current || !sessionFresh) return;
    if (peekCollapseTimer.current) clearTimeout(peekCollapseTimer.current);
    peekCollapseTimer.current = setTimeout(() => setPeek(true), 500);
  };

  // ── Peek — Reco's opening line, and nothing else ────────────────────────────
  if (peek && !minimized && !isInline) {
    return (
      <div className="fixed bottom-6 right-4 z-[1003] w-[min(22rem,calc(100vw-2rem))]">
        <button
          type="button"
          onClick={() => openFromPeek(false)}
          onFocus={() => openFromPeek(false)}
          onMouseEnter={onPeekHover}
          onMouseLeave={cancelPeekHover}
          aria-label={ra.openChat}
          className="group flex w-full items-start gap-2.5 text-left focus:outline-none"
        >
          <img src={CRANE_MARK} alt="" aria-hidden className="mt-1 h-6 w-6 flex-shrink-0" />
          <span className="w-fit rounded-2xl rounded-tl-md bg-crane-500 px-3.5 py-2.5 text-body text-ink-950 shadow-xl shadow-black/50 transition-transform duration-200 group-hover:-translate-y-0.5 group-focus-visible:-translate-y-0.5">
            {ra.welcome}
          </span>
        </button>
      </div>
    );
  }

  if (minimized && !isInline) {
    return (
      <div className="fixed right-4 bottom-6 z-[1003]">
        <button
          type="button"
          onClick={() => { setPeek(false); setMinimized(false); }}
          aria-label={ra.openChat}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-crane-500/40 bg-ink-900 shadow-lg shadow-black/40 transition-colors hover:border-crane-500/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500"
        >
          <img src={CRANE_MARK} alt="" aria-hidden className="h-6 w-6" />
        </button>
      </div>
    );
  }

  // ── Full panel ──────────────────────────────────────────────────────────────
  return (
    <div
      className={isInline
        ? 'flex min-h-0 w-full flex-1 flex-col'
        : 'fixed right-4 bottom-6 z-[1003] w-[min(480px,calc(100vw-2rem))] rounded-2xl border border-ink-700/80 bg-ink-950/85 p-2 shadow-2xl'}
      style={isInline ? undefined : backdropStyle}
      onMouseEnter={isInline ? undefined : onPanelEnter}
      onMouseLeave={isInline ? undefined : onPanelLeave}
      onPointerDown={isInline ? undefined : () => { openedByHoverRef.current = false; }}
    >

      {/* Message history. Inline = full-page chat (no frame, the page scrolls);
          floating = a bordered panel with its own scroller. */}
      <div
        ref={setScroller}
        onScroll={onScroll}
        onWheel={onUserScrollIntent}
        onTouchMove={onUserScrollIntent}
        onPointerDown={onUserScrollIntent}
        onKeyDown={onUserScrollIntent}
        className={isInline
          ? 'min-h-0 flex-1 overflow-y-auto'
          : 'mb-2 max-h-[min(46dvh,380px)] overflow-y-auto rounded-xl border border-ink-700 bg-ink-900'}>

        {/* Header. Inline needs no title bar — the page heading names Reco — so
            it keeps only the status chip and the two icon actions, unframed. */}
        <div className={isInline
          ? 'sticky top-0 z-30 flex items-center justify-end gap-2 bg-ink-950 pb-2 pt-1'
          : 'sticky top-0 z-10 rounded-t-xl border-b border-ink-700 bg-ink-900'}>
          <div className={isInline ? 'flex items-center gap-2' : 'flex items-center gap-2.5 px-3.5 py-3'}>
            {!isInline && (
              <>
                <img src={CRANE_MARK} alt="" aria-hidden className="h-5 w-5 flex-shrink-0" />
                <p className="min-w-0 flex-1 truncate text-title font-medium text-ink-100">{ra.title}</p>
              </>
            )}

            {statusLabel && (
              <span className="flex-shrink-0 rounded-full border border-crane-500/30 bg-crane-500/10 px-2 py-0.5 text-meta font-medium text-crane-500">
                {statusLabel}
              </span>
            )}

            {/* Switching presentation is now just a navigation: the session
                lives above the router, so the transcript, the chosen extent and
                any upload in flight are already there on the other side. */}
            <IconAction
              onClick={() => navigate(isInline ? '/' : '/reco')}
              label={isInline ? ra.collapseChat : ra.expandChat}
            >
              {isInline
                ? <Minimize2 className="h-4 w-4" />
                : <Maximize2 className="h-4 w-4" />}
            </IconAction>

            <IconAction onClick={resetSession} label={ra.restartSession}>
              <RefreshCcw className="h-4 w-4" />
            </IconAction>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={setList}
          // Anyone who would rather read it now than watch it arrive.
          onClick={revealAll}
          className={isInline ? 'space-y-5 py-2' : 'space-y-4 p-4'}
        >
          {messages.slice(0, revealed ?? messages.length).map((msg, i) => (
            <div key={i}>
              {msg.role === 'system' ? (
                <div className="flex items-center gap-3 py-1.5">
                  <div className="h-px flex-1 bg-ink-700" />
                  <span className="flex-shrink-0 text-meta text-ink-500">{msg.content}</span>
                  <div className="h-px flex-1 bg-ink-700" />
                </div>
              ) : (
                <>
                  {/*
                    * A settled extent or an approved set of previews is something
                    * the *user* decided, so it is said in their voice and sits on
                    * their side. The control cannot go inside a solid ember
                    * bubble — a map and a photo carousel both carry their own
                    * dark styling and would fight a bright fill — so the card is
                    * ember-tinted and bordered instead, and the sentence sits
                    * above the thing it refers to.
                    */}
                  {msg.content && (
                    <div className={
                      msg.role === 'user'
                        ? 'ml-auto w-fit max-w-[min(88%,44ch)] rounded-2xl rounded-tr-md bg-ember-500 px-3.5 py-2.5 text-body text-ink-950 sm:max-w-[min(72%,44ch)]'
                        : 'w-fit max-w-[min(92%,50ch)] rounded-2xl rounded-tl-md bg-crane-500 px-3.5 py-2.5 text-body text-ink-950 sm:max-w-[min(76%,50ch)]'
                    }>
                      {msg.role !== 'assistant'
                        ? msg.content
                        : i === typingIndex
                          ? <TypedMarkdown text={msg.content} onDone={() => setTypingIndex(null)} />
                          : <AgentMarkdown text={msg.content} />
                      }
                    </div>
                  )}

                  {/*
                    * The control follows its own bubble rather than sitting in it:
                    * a map and a photo carousel carry their own dark styling and
                    * would fight a solid ember fill. Tinted and bordered on the
                    * user's side keeps the two reading as one statement.
                    */}
                  {(msg.extent || msg.imageToken) && (
                    <div className={`w-full max-w-[min(94%,34rem)] rounded-2xl border p-2.5 ${msg.content ? 'mt-2' : ''} ${
                      msg.role === 'user'
                        ? 'ml-auto rounded-tr-md border-ember-500/40 bg-ember-500/[0.08]'
                        : 'rounded-tl-md border-ink-700 bg-ink-900/60'
                    }`}>
                      {msg.extent && (
                        <AddressExtentPicker
                          center={{ lat: msg.extent.lat, lon: msg.extent.lon }}
                          address={msg.extent.address}
                          radiusM={msg.extent.radiusM}
                          onRadiusChange={() => {}}
                          compact
                          readOnly
                        />
                      )}
                      {msg.imageToken && (
                        // Settled: nothing left to report, so the callbacks are inert.
                        <ImageryPreviewPanel token={msg.imageToken} views={msg.imageViews} compact onReady={noop} onFailed={noop} />
                      )}
                    </div>
                  )}
                  {msg.files && msg.files.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-meta font-medium uppercase tracking-wide text-ink-500">{ra.filesGenerated}</p>
                      {msg.files.map((f) => (
                        <div key={f} className="rounded-md border border-ink-700 bg-ink-850 px-2.5 py-1.5">
                          <code className="break-all text-meta text-ink-300">{f}</code>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Reco's capture entry. These belong in the transcript, attached to his
              message, so they read as something he is offering — not as chrome
              bolted onto the input. Typing instead of clicking still works. */}
          {awaitingChoice && (
            <div className="max-w-[60ch] space-y-2 pt-1">
              <p className="text-meta text-ink-400">{ra.selectToProceed}</p>
              <div className="grid grid-cols-1 gap-2">
                {([
                  { key: 'images'  as const, label: ra.optionOwnData, hint: ra.optionOwnDataHint, msg: ra.optionOwnDataMsg },
                ]).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => chooseOption(opt.key, opt.msg)}
                    disabled={loading}
                    className="group flex items-start justify-between gap-3 rounded-xl border border-ink-700 bg-ink-850 p-3.5 text-left transition-colors enabled:hover:border-crane-500/50 enabled:hover:bg-ink-800 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500"
                  >
                    <span className="min-w-0">
                      <span className="block text-ui font-medium text-ink-100">{opt.label}</span>
                      <span className="mt-1 block text-meta text-ink-400">{opt.hint}</span>
                    </span>
                    <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-500 transition-colors group-hover:text-crane-500" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Typing / connecting indicator */}
          {loading && (
            <div className="flex w-fit items-center gap-1.5 rounded-2xl rounded-tl-md bg-crane-500 px-3.5 py-3">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink-950/60" style={{ animationDelay: '0ms' }} />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink-950/60" style={{ animationDelay: '150ms' }} />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink-950/60" style={{ animationDelay: '300ms' }} />
            </div>
          )}

          {/* Done state — only once the message announcing it has finished
              arriving, like every other control. */}
          {phase === 'done' && checkPassed && transcriptSettled && (
            <div className="rounded-xl border border-crane-500/25 bg-crane-500/[0.07] p-4 text-center">
              <div className="mx-auto mb-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-crane-500/15">
                <Check className="h-4 w-4 text-crane-500" />
              </div>
              <p className="text-body font-medium text-ink-100">{ra.doneTitle}</p>
              <p className="mt-1 text-ui text-ink-300">
                {doneKind === 'worldmap' ? ra.doneNoteWorldMap : ra.doneNote}
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-ui text-danger">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              {error}
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Composer. Inline: sticky to the viewport bottom with a fade above, so
          the conversation scrolls underneath it. Floating: a plain card. */}
      {(pendingAction || showComposer) && (
        <div
          className={isInline
          ? 'flex-shrink-0 pt-3'
          : ''}>
        <div className="overflow-hidden rounded-xl border border-ink-700 bg-ink-900">

          {/* scrollbar-gutter reserves the bar's width whether or not one is
              showing, so content neither shifts as it appears nor gets clipped by
              it: `overflow-y-auto` computes overflow-x to auto as well, and the
              bar is laid out inside the content box. */}
          {pendingAction && (
            <div className="max-h-[min(46dvh,380px)] space-y-2 overflow-y-auto p-2 pb-0 [scrollbar-gutter:stable]">
          {/* Images tab — share link only */}
          {phase === 'intake' && tab === 'images' && (
            <div className="px-1 py-0.5 text-ui text-ink-200">
              <p>{ra.uploadIntro} <span className="text-ink-500">{ra.uploadHosts}</span></p>
            </div>
          )}

          {/* Address tab callout */}
          {phase === 'intake' && tab === 'address' && !vexcelCoverage && (
            <p className="px-1 py-0.5 text-ui text-ink-200">{ra.addressIntro}</p>
          )}
          {/* Sample imagery (preview flow only). Coverage and the presence of all
              five views are already settled by the time this mounts; what is
              still in question is whether we can fetch the pixels, which is what
              the panel reports back. Buttons appear only once every view has
              arrived — deciding on a half-loaded carousel is deciding blind. */}
          {/* Held back until the "Verified!" bubble has arrived *and* finished
              typing. The panel must mount as soon as the phase starts — mounting
              is what fetches the five views — but showing it straight away put
              the carousel on screen ahead of the sentence that introduces it,
              filling in behind the text as the images trickled in. `hidden`
              rather than unmounting, so the fetch is untouched and the buttons
              appear the moment the sentence lands. */}
          {phase === 'imagery-preview' && vexcelCoverage?.imageToken && (
            <div className="space-y-2 px-1 py-0.5" hidden={!previewReady}>
              <ImageryPreviewPanel
                token={vexcelCoverage.imageToken}
                views={vexcelCoverage.imageViews}
                compact={!isInline}
                onReady={onPreviewReady}
                onFailed={onPreviewFailed}
              />
              {previewReady && (
                <div className="flex gap-2">
                  <button type="button" onClick={acceptPreview} className={BTN_PRIMARY}>
                    {ra.previewStart}
                  </button>
                  <button type="button" onClick={declinePreview} className={BTN_QUIET}>
                    {ra.coverageDecline}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Why they backed out. Fixed options rather than a free-text turn: it
              routes without an intake round-trip, and three buckets are worth
              more than prose nobody aggregates. */}
          {/* One column rather than a wrapping row. These labels differ in
              length by a factor of three, so flex-wrap produced ragged rows with
              a lonely third button and text sitting off-centre in its own box. A
              grid gives every option the same width; items-center with a minimum
              height keeps one-line and two-line labels on the same baseline. */}
          {phase === 'cancel-reason' && (
            <div className="grid grid-cols-1 gap-2 px-1 py-0.5">
              {(cancelKind === 'email'
                ? [ra.cancelEmailNoEmail, ra.cancelEmailTrying, ra.cancelEmailOther]
                : [ra.cancelReasonQuality, ra.cancelReasonCoverage, ra.cancelReasonOther]
              ).map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => submitCancelReason(label)}
                  className="flex min-h-[2.75rem] w-full items-center justify-center rounded-lg border border-ink-600 bg-ink-850 px-3 py-2 text-center text-ui font-medium text-ink-200 transition-colors hover:border-crane-500/60 hover:bg-ink-800 hover:text-ink-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500"
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Email capture. A field of its own rather than the chat composer: the
              composer is hidden for this phase, so there is exactly one place to
              type, and the two buttons make going ahead and backing out equally
              visible. type=email gets the right keyboard on mobile. */}
          {phase === 'email-capture' && (
            <div className="space-y-2 px-1 py-0.5">
              {/* Optional, and carried by their placeholders alone — two more
                  boxes above a required one already reads as a form, and labels
                  would make it look longer than it is. */}
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !vexcelProceedLoading) { e.preventDefault(); submitEmail(); } }}
                placeholder={ra.namePlaceholder}
                autoComplete="name"
                disabled={vexcelProceedLoading}
                aria-label={ra.namePlaceholder}
                className={FIELD_CLASS}
              />
              <input
                type="text"
                value={companyInput}
                onChange={(e) => setCompanyInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !vexcelProceedLoading) { e.preventDefault(); submitEmail(); } }}
                placeholder={ra.companyPlaceholder}
                autoComplete="organization"
                disabled={vexcelProceedLoading}
                aria-label={ra.companyPlaceholder}
                className={FIELD_CLASS}
              />
              <input
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !vexcelProceedLoading) { e.preventDefault(); submitEmail(); } }}
                placeholder={`${ra.emailPlaceholder} (${ra.emailRequiredMark})`}
                autoComplete="email"
                inputMode="email"
                disabled={vexcelProceedLoading}
                aria-required="true"
                aria-label={`${ra.emailPlaceholder} (${ra.emailRequiredMark})`}
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'reco-email-error' : undefined}
                className={`w-full rounded-lg border bg-ink-850 px-3 py-2 text-body text-ink-100 caret-crane-500 outline-none transition-colors placeholder:text-ink-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                  emailError ? 'border-danger' : 'border-ink-600 focus:border-crane-500/60'
                }`}
              />
              {emailError && (
                <p id="reco-email-error" role="alert" className="text-meta text-danger">{emailError}</p>
              )}
              <div className="flex gap-2">
                {/* Live even with the field empty — pressing it is how someone who
                    missed the field finds out one is needed. */}
                <button
                  type="button"
                  onClick={submitEmail}
                  disabled={vexcelProceedLoading}
                  className={BTN_PRIMARY}
                >
                  {vexcelProceedLoading ? ra.extentChecking : ra.emailStart}
                </button>
                <button
                  type="button"
                  onClick={handleVexcelCancel}
                  disabled={vexcelProceedLoading}
                  className={BTN_QUIET}
                >
                  {ra.coverageDecline}
                </button>
              </div>
            </div>
          )}

          {/* Vexcel coverage confirmation card */}
          {phase === 'vexcel-confirm' && vexcelCoverage && (
            <div className="space-y-2 px-1 py-0.5">
              {/* Once coverage is settled the map goes read-only. /proceed does not
                  re-verify, so a pin moved after the check would reconstruct a
                  rectangle nobody validated. readOnly also drops the expand
                  affordance, which is the price of freezing it in one flag. */}
              <AddressExtentPicker
                caption={ra.extentIntro}
                center={{ lat: vexcelCoverage.centerLat, lon: vexcelCoverage.centerLon }}
                address={vexcelCoverage.placeName ?? vexcelCoverage.worldMapIntake.address}
                radiusM={radiusM}
                onRadiusChange={setRadiusM}
                compact={!isInline}
                disabled={vexcelProceedLoading}
                readOnly={coverageConfirmed}
                onCenterChange={coverageConfirmed ? undefined : ({ lat, lon }) =>
                  setVexcelCoverage((prev) => (prev ? { ...prev, centerLat: lat, centerLon: lon } : prev))
                }
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={checkCoverage}
                  disabled={vexcelProceedLoading || coverageConfirmed}
                  className={BTN_PRIMARY}
                >
                  {vexcelProceedLoading ? ra.extentChecking : ra.extentCheckCoverage}
                </button>
                <button
                  type="button"
                  onClick={handleVexcelCancel}
                  disabled={vexcelProceedLoading}
                  className={BTN_SECONDARY}
                >
                  {ra.cancel}
                </button>
              </div>
            </div>
          )}

          {/* Warning action buttons */}
          {pendingWarning && (
            <div className="space-y-2 rounded-xl border border-ink-700 bg-ink-850 p-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleProceed}
                  disabled={warningActionLoading}
                  className={BTN_PRIMARY}
                >
                  {warningActionLoading ? ra.warningWorking : ra.warningProceed}
                </button>
                <button
                  type="button"
                  onClick={handleRevise}
                  disabled={warningActionLoading}
                  className={BTN_SECONDARY}
                >
                  {warningActionLoading ? ra.warningWorking : ra.warningRevise}
                </button>
              </div>
            </div>
          )}
            </div>
          )}

          {showComposer && (
            <div className="m-2 flex items-center gap-1.5 rounded-xl border border-ink-700 bg-ink-850 p-1.5 pl-3.5 transition-colors focus-within:border-crane-500/50">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={inputDisabled}
            placeholder={inputPlaceholder}
            className="min-w-0 flex-1 bg-transparent text-body text-ink-100 caret-crane-500 outline-none placeholder:text-ink-500 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={send}
            disabled={!input.trim() || inputDisabled}
            aria-label={ra.send}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-crane-500 text-ink-950 transition-colors hover:bg-crane-600 disabled:cursor-not-allowed disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-400"
          >
            <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={toggle}
            onMouseDown={(e) => e.preventDefault()}
            aria-label={ra.toggleLang}
            className="flex h-8 shrink-0 items-center gap-0.5 rounded-lg px-2 text-meta font-medium tracking-widest text-ink-500 transition-colors hover:text-ink-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500"
          >
            <span style={{ opacity: lang === 'fr' ? 1 : 0.35 }}>FR</span>
            <span className="opacity-30">|</span>
            <span style={{ opacity: lang === 'en' ? 1 : 0.35 }}>EN</span>
          </button>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            aria-label={ra.minimize}
            className={`h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-500 transition-colors hover:text-ink-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500 ${isInline ? 'hidden' : 'flex'}`}
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  );
};

/*
 * I18nProvider used to be wrapped here, which gave every mount its own language
 * state — so toggling to French and navigating reverted the language along with
 * everything else. It sits in App.tsx now, above both the router and the
 * session, so the two agree on which locale seeded the transcript.
 */
export default ReconstructionAgentPanel;
